import {createSlice, type PayloadAction} from '@reduxjs/toolkit';
import type {RootState} from '../../../store/store';
import type {CallPanel, EndedCall} from '../model/types';
import type {CallSurfaceUi} from '../model/view';
import type {InternalCallee} from '../matrix/matrixActions';

export type DialerNotice = 'in_call' | 'sip_error' | 'no_number';

/** A dialled number refused by call restrictions (403 + X-PHQ-Refusal). */
export interface DialerRefusal {
    /** What was dialled, so the notice goes away once the number changes. */
    readonly number: string;
    readonly source?: string;
    readonly label?: string;
}

export interface DirectPrepRequest {
    readonly roomId: string;
    readonly peer: InternalCallee & {readonly avatarUrl?: string};
    readonly intent: 'audio' | 'video';
}

export interface DndCallRequest {
    readonly video?: boolean;
    readonly roomId?: string;
}

export type CallSurfaceState = Omit<CallSurfaceUi, 'dialerOpen'> & {
    readonly dndPrompt?: DndCallRequest;
    readonly dialerNotice?: DialerNotice;
    readonly dialerRefusal?: DialerRefusal;
    readonly directPrep?: DirectPrepRequest;
    readonly pillSuppressions: number;
};

const initialState: CallSurfaceState = {
    dndPrompt: undefined,
    dialerNotice: undefined,
    dialerRefusal: undefined,
    expanded: false,
    panel: 'none',
    ended: undefined,
    pillSuppressions: 0,
};

const callSurfaceSlice = createSlice({
    name: 'callSurface',
    initialState,
    reducers: {
        setExpanded: (state, action: PayloadAction<boolean>) => {
            state.expanded = action.payload;
        },
        setPanel: (state, action: PayloadAction<CallPanel>) => {
            state.panel = action.payload;
        },
        openDirectPrep: (state, action: PayloadAction<DirectPrepRequest>) => {
            state.directPrep = action.payload;
        },
        closeDirectPrep: state => {
            state.directPrep = undefined;
        },
        togglePanel: (state, action: PayloadAction<Exclude<CallPanel, 'none'>>) => {
            state.panel = state.panel === action.payload ? 'none' : action.payload;
        },
        callEnded: (state, action: PayloadAction<EndedCall>) => {
            state.ended = action.payload;
            state.expanded = false;
            state.panel = 'none';
        },
        dismissEnded: (state, action: PayloadAction<string | undefined>) => {
            if (!action.payload || state.ended?.legId === action.payload) {
                state.ended = undefined;
            }
        },
        callStarted: state => {
            state.ended = undefined;
            state.dialerRefusal = undefined;
            state.dndPrompt = undefined;
        },
        promptDndCall: (state, action: PayloadAction<DndCallRequest | undefined>) => {
            state.dndPrompt = action.payload ?? {};
        },
        dismissDndCall: state => {
            state.dndPrompt = undefined;
        },
        setDialerNotice: (state, action: PayloadAction<DialerNotice | undefined>) => {
            state.dialerNotice = action.payload;
        },
        setDialerRefusal: (state, action: PayloadAction<DialerRefusal | undefined>) => {
            state.dialerRefusal = action.payload;
        },
        suppressStartCallPill: state => {
            state.pillSuppressions += 1;
        },
        releaseStartCallPill: state => {
            state.pillSuppressions = Math.max(0, state.pillSuppressions - 1);
        },
    },
});

export const {
    setExpanded,
    setPanel,
    togglePanel,
    callEnded,
    dismissEnded,
    openDirectPrep,
    closeDirectPrep,
    callStarted,
    promptDndCall,
    dismissDndCall,
    setDialerNotice,
    setDialerRefusal,
    suppressStartCallPill,
    releaseStartCallPill,
} = callSurfaceSlice.actions;

export const selectCallSurfaceState = (state: RootState): CallSurfaceState => state.callSurface;

export const selectStartCallPillSuppressed = (state: RootState): boolean => state.callSurface.pillSuppressions > 0;

export const selectDialerOpen = (state: RootState): boolean => !state.dialer.isDialerHidden;

export default callSurfaceSlice.reducer;
